import { useState, useEffect, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import type { FeatureItem } from "../types/feature";
import { supabase } from "../lib/supabaseClient";
import {
  getSupabaseCategoryImageUrl,
  DEFAULT_CATEGORY_ICON,
} from "../utils/imageUtils";

// ============================================================================
// CART & WISHLIST PLACEHOLDERS
// ============================================================================

export const useCart = (userId?: string | null) => {
  userId = userId || null;
  return {
    cartItems: [],
    cartCount: 0,
    cartTotal: 0,
    loading: false,
    error: null,
    addToCart: async () => {},
    removeFromCart: async () => {},
    clearCart: async () => {},
    refreshCart: async () => {},
  };
};

export const useWishlist = (userId?: string | null) => {
  userId = userId || null;
  return {
    wishlistItems: [],
    wishlistCount: 0,
    loading: false,
    error: null,
    addToWishlist: async () => {},
    removeFromWishlist: async () => {},
    isInWishlist: () => false,
    refreshWishlist: async () => {},
  };
};

// ============================================================================
// FEATURE / CATEGORY HOOKS
// ============================================================================

interface UseSupabaseFeaturesOptions {
  categoryId?: number;
  enabled?: boolean;
  includeProductCount?: boolean;
  limit?: number;
  offset?: number;
  locale?: string;
}

interface UseSupabaseFeaturesReturn {
  features: FeatureItem[];
  loading: boolean;
  error: string | null;
  refetch: () => void;
}

export const useSupabaseFeatures = ({
  categoryId,
  enabled = true,
  includeProductCount = false,
  limit = 20,
  offset = 0,
  locale = "en",
}: UseSupabaseFeaturesOptions = {}): UseSupabaseFeaturesReturn => {
  const [features, setFeatures] = useState<FeatureItem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchFeatures = useCallback(async () => {
    if (!enabled) return;

    setLoading(true);
    setError(null);

    try {
      const { data, error: fetchError } = await supabase.rpc(
        "fetch_categories_having_subcategories_and_products",
        {
          p_limit: limit,
          p_offset: offset,
          p_locale: locale,
        },
      );

      if (fetchError) throw fetchError;

      if (data && Array.isArray(data)) {
        const transformedFeatures: FeatureItem[] = data.map(
          (category: any) => ({
            id: category.id.toString(),
            name: category.name,
            image:
              getSupabaseCategoryImageUrl(category.icon_path) ||
              DEFAULT_CATEGORY_ICON,
            productCount: includeProductCount ? category.product_count || 0 : 0,
            link: `/category/${category.id}`,
          }),
        );

        const filteredFeatures = categoryId
          ? transformedFeatures.filter(
              (feature) => feature.id === categoryId.toString(),
            )
          : transformedFeatures;

        setFeatures(filteredFeatures);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to fetch features");
    } finally {
      setLoading(false);
    }
  }, [categoryId, enabled, includeProductCount, limit, offset, locale]);

  useEffect(() => {
    fetchFeatures();
  }, [fetchFeatures]);

  return { features, loading, error, refetch: fetchFeatures };
};

// ============================================================================
// CATEGORIES WITH PRODUCTS (used by Header, ProductPage, FeatureOne)
// ============================================================================

type SubCategory = {
  id: number;
  name: string;
  created_at: string;
  description: string;
  icon_path?: string;
  is_hidden: boolean;
  sub_categories: SubCategory[];
};

type Category = {
  id: number;
  name: string;
  created_at: string;
  description: string;
  icon_path?: string;
  sub_categories: SubCategory[];
};

interface UseCategoriesResult {
  categoriesWithSubs: Category[];
  loading: boolean;
  error: string | null;
}

function mapToSubCategories(children: any[]): SubCategory[] {
  return children.map((child) => ({
    id: child.id,
    name: child.name,
    description: child.description ?? "",
    icon_path: child.icon_path ?? undefined,
    created_at: child.created_at,
    is_hidden: child.is_hidden ?? false,
    sub_categories: child.children ? mapToSubCategories(child.children) : [],
  }));
}

function mapToCategories(categories: any[]): Category[] {
  return categories.map((cat) => ({
    id: cat.id,
    name: cat.name,
    description: cat.description ?? "",
    icon_path: cat.icon_path ?? undefined,
    created_at: cat.created_at,
    sub_categories: cat.children ? mapToSubCategories(cat.children) : [],
  }));
}

export function useCategoriesWithProducts(): UseCategoriesResult {
  const { data, isLoading, error } = useQuery({
    queryKey: ["categories-with-products"],
    queryFn: async () => {
      const { data, error } = await supabase.rpc(
        "get_categories_with_children_v2h",
        { payload: {} },
      );
      if (error) throw new Error(error.message);

      return Array.isArray((data as any)?.data)
        ? mapToCategories((data as any).data)
        : [];
    },
    staleTime: 5 * 60 * 1000,
    gcTime: 10 * 60 * 1000,
  });

  return {
    categoriesWithSubs: data ?? [],
    loading: isLoading,
    error: error?.message ?? null,
  };
}

// ============================================================================
// CATEGORIES FOR FILTERS (used by ProductPage sidebar)
// ============================================================================

interface ProductFilterCategory {
  id: number;
  name: string;
  product_count?: number;
}

interface ProductFilterSubCategory {
  id: number;
  name: string;
  category_id: number;
  product_count?: number;
}

interface UseCategoriesForFiltersResult {
  categories: ProductFilterCategory[];
  subCategories: ProductFilterSubCategory[];
  loading: boolean;
  error: string | null;
}

export const useCategoriesForFilters = (): UseCategoriesForFiltersResult => {
  const [categories, setCategories] = useState<ProductFilterCategory[]>([]);
  const [subCategories, setSubCategories] = useState<
    ProductFilterSubCategory[]
  >([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        setError(null);

        const { data, error: fetchError } = await supabase.rpc(
          "get_categories_with_children_v2h",
          {
            payload: {},
          },
        );

        if (fetchError) throw fetchError;

        let categoriesData: any[] = [];

        if (
          data &&
          typeof data === "object" &&
          "data" in data &&
          Array.isArray((data as any).data)
        ) {
          categoriesData = (data as any).data;
        } else if (Array.isArray(data)) {
          categoriesData = data;
        } else {
          categoriesData = [];
        }

        const flatCategories: ProductFilterCategory[] = [];
        const flatSubCategories: ProductFilterSubCategory[] = [];

        categoriesData.forEach((category: any) => {
          flatCategories.push({
            id: category.id,
            name: category.name,
            product_count: category.product_count || 0,
          });

          if (category.children && Array.isArray(category.children)) {
            category.children.forEach((subCategory: any) => {
              flatSubCategories.push({
                id: subCategory.id,
                name: subCategory.name,
                category_id: category.id,
                product_count: subCategory.product_count || 0,
              });
            });
          }
        });

        setCategories(flatCategories);
        setSubCategories(flatSubCategories);
      } catch (err) {
        setError(
          err instanceof Error
            ? err.message
            : "Failed to fetch categories and subcategories",
        );
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  return { categories, subCategories, loading, error };
};

// ============================================================================
// CART OPERATIONS
// ============================================================================

export const addProductToCart = async ({
  productId,
  quantity = 1,
}: {
  productId: number;
  quantity?: number;
}) => {
  if (!productId) throw new Error("Product ID is required");

  const payload = [{ id: productId, qty: quantity }];
  const { data, error } = await supabase.rpc("user_card_add_by_ids_v2h", {
    payload,
  });
  if (error) throw error;
  return data;
};

export const getUserCardGetV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("get_user_card_get_v2", {
    payload,
  });
  if (error) throw error;
  return data;
};

export const deleteUserCardV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("delete_user_card_v2", {
    payload,
  });
  if (error) throw error;
  return data;
};

// ============================================================================
// ADDRESS OPERATIONS
// ============================================================================

export const getAddressWithTypeV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("get_address_with_type_v2", {
    payload,
  });
  if (error) throw error;
  return data;
};

export const addEditAddressV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("add_edit_address_v2", {
    payload,
  });
  if (error) throw error;
  return data;
};

export const addressSetDefaultV2 = async (payload: { id: number }) => {
  const { data, error } = await supabase.rpc("address_set_default_v2", {
    payload,
  });
  if (error) throw error;
  return data;
};

export const deleteAddressV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("delete_address_v2", { payload });
  if (error) throw error;
  return data;
};

// ============================================================================
// ORDER OPERATIONS
// ============================================================================

export const addOrderV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("add_order_v2", { payload });
  if (error) throw error;
  return data;
};

export const getStagesV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("get_stages_v2", { payload });
  if (error) throw error;
  return data;
};

export const getOrdersV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("get_orders_v2", { payload });
  if (error) throw error;
  return data;
};

export const getOrdersByStageV2 = async (payload: Record<string, any>) => {
  const { data, error } = await supabase.rpc("get_orders_by_stage_v2", {
    payload,
  });
  if (error) throw error;
  return data;
};

// ============================================================================
// PRODUCT RECOMMENDATIONS (still used by useProductDetail)
// ============================================================================

export const getProductRecommendationsV2 = async (
  payload: Record<string, any>,
) => {
  const { data, error } = await supabase.rpc(
    "get_product_recommendations_v2h",
    { payload },
  );
  if (error) throw error;
  return data;
};
